/**
 * 精霊の顔のアップ（2026-10-12・ホームの精霊のしおりの帯）。精霊の原本（spiritMaster/{id}.faceFocus）に、顔の中心の位置と寄せる倍率を持つ。
 * 管理UIの精霊の編集で決め、アプリと管理UIの見本は同じ計算（faceFocusStyle）で絵を寄せる。
 * 管理UI（local-ui）も写しで読むので、import は書かないこと
 */

export interface SpiritFaceFocus {
  /** 顔の中心の横の位置（絵の幅に対する割合。0＝左端・1＝右端） */
  x: number;
  /** 顔の中心の縦の位置（絵の高さに対する割合。0＝上端・1＝下端） */
  y: number;
  /** 寄せる倍率（1＝枠いっぱいに絵の全体） */
  zoom: number;
}

export const FACE_ZOOM_MIN = 1;
export const FACE_ZOOM_MAX = 4;

/** 決めていない精霊は、真ん中の上寄りを2倍に寄せる */
export const DEFAULT_FACE_FOCUS: SpiritFaceFocus = { x: 0.5, y: 0.35, zoom: 2 };

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** 小さな枠（精霊の一覧の額・地図の出会える精霊の写真）で使う寄せ幅の割合（1＝しおりと同じ・0＝全身）。10/19 本人「少しアップすぎる」 */
export const SOFT_FACE_SHARE = 0.5;

/** 寄せ幅を弱めた顔の位置（決めていない精霊は既定の2倍→1.5倍）。顔の点はそのまま */
export function softenFaceFocus(focus: SpiritFaceFocus | null | undefined, share = SOFT_FACE_SHARE): SpiritFaceFocus {
  const f = focus ?? DEFAULT_FACE_FOCUS;
  return { ...f, zoom: 1 + (f.zoom - 1) * share };
}

/** 保存された値を読む。読めなければ null（DEFAULT_FACE_FOCUS で寄せる） */
export function parseFaceFocus(raw: unknown): SpiritFaceFocus | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const x = Number(r.x), y = Number(r.y), zoom = Number(r.zoom);
  if (![x, y, zoom].every(Number.isFinite)) return null;
  return { x: clamp(x, 0, 1), y: clamp(y, 0, 1), zoom: clamp(zoom, FACE_ZOOM_MIN, FACE_ZOOM_MAX) };
}

/**
 * 枠いっぱいに敷いた絵（object-fit: cover）を、顔の中心が枠の真ん中（縦は少し上の 0.42）に来るよう寄せる見た目。
 * 絵は object-position で顔の点を枠の同じ割合の点に合わせ、その点を中心に拡大してから、真ん中へずらす。
 * ずらしは、拡大した絵が枠からはみ出さない（隙間が出ない）範囲に留める
 */
export function faceFocusStyle(focus: SpiritFaceFocus | null | undefined): { objectPosition: string; transformOrigin: string; transform: string } {
  const f = focus ?? DEFAULT_FACE_FOCUS;
  const s = f.zoom;
  const shift = (o: number, target: number) => clamp(target - o, (1 - o) * (1 - s), o * (s - 1));
  const dx = shift(f.x, 0.5) * 100;
  const dy = shift(f.y, 0.42) * 100;
  const at = `${(f.x * 100).toFixed(1)}% ${(f.y * 100).toFixed(1)}%`;
  return { objectPosition: at, transformOrigin: at, transform: `translate(${dx.toFixed(1)}%, ${dy.toFixed(1)}%) scale(${s})` };
}
